import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useHistory, useLocation, Prompt } from 'react-router-dom';
import { Location } from 'history';
import { useSnackbar } from 'notistack';
import IconButton from '@mui/material/IconButton';
import CloseIconMui from '@mui/icons-material/Close';

import useMst from '~/src/hooks/useMst';
import useFetchTemplateDefaultValues from '~/src/hooks/useFetchTemplateDefaultValues';
import useBackend from '~/src/hooks/useBackend';

import { useWebViewerContext } from '~/src/components/Webviewer';
import { Text } from '~/src/components/Typography';

import { LAYOUT_FOOTER_TYPES, useLayoutContext } from '~/src/contexts/Layout';
import { LAYOUT_MODAL_TYPES } from '~/src/components/PageLayout/Modals';
import { LAYOUT_TOAST_TYPES } from '~/src/components/PageLayout/Toasts';
import { LoadingOverlay } from '~/src/components/PageLayout';
import { NAVIGATION_WIDTH } from '~/src/components/PageLayout/styles';

import { ReactComponent as CloseIcon } from '~/src/static/close-icon.svg';

import {
  toDocumentElements,
  extractFieldValues,
  normalizeFieldValue,
} from '~/src/utils/formTemplateAdapter';

import analyticsService from '~/src/services/analytics';

const FormTemplateDraftingContainer = ({
  templateDefaultValuesID,
  duplicate,
}: {
  templateDefaultValuesID: number;
  duplicate: boolean;
}) => {
  const templateDefaultValuesResponse = useFetchTemplateDefaultValues(
    templateDefaultValuesID,
  );

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const deprecationSnackbarKeyRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!templateDefaultValuesResponse?.isSourceDeprecated) return;
    if (deprecationSnackbarKeyRef.current !== null) return;
    const key = enqueueSnackbar(
      <>
        {`This questionnaire contains an older version of `}
        {(templateDefaultValuesResponse.template?.title as string | null) ?? (
          <em style={{ marginLeft: '0.25em' }}>
            {templateDefaultValuesResponse.template?.subtitle}
          </em>
        )}
      </>,
      {
        variant: 'warning',
        persist: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        action: (snackbarId) => (
          <IconButton
            size="small"
            onClick={() => closeSnackbar(snackbarId)}
            sx={{ color: 'inherit' }}
          >
            <CloseIconMui fontSize="small" />
          </IconButton>
        ),
      },
    );
    deprecationSnackbarKeyRef.current = key;
  }, [templateDefaultValuesResponse, enqueueSnackbar, closeSnackbar]);

  useEffect(() => {
    const keyRef = deprecationSnackbarKeyRef;
    return () => {
      if (keyRef.current !== null) closeSnackbar(keyRef.current);
    };
  }, [closeSnackbar]);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [backendReady, setBackendReady] = useState(false);
  const prevTemplateId = useRef(templateDefaultValuesID);
  const customFieldDataRef = useRef<Record<string, string>>({});
  const liveFieldValuesRef = useRef<Record<string, string>>({});

  if (prevTemplateId.current !== templateDefaultValuesID) {
    prevTemplateId.current = templateDefaultValuesID;
    setBackendReady(false);
    liveFieldValuesRef.current = {};
  }

  const { onSubmit, clearError, isLoading } = useMst((store) => ({
    onSubmit: store.templateDefaultValues.submitTemplateDefaultValues,
    clearError: store.templateDefaultValues.clearError,
    isLoading: store.templateDefaultValues.isLoading,
  }));

  const history = useHistory();
  const location = useLocation();
  const [lastLocation, setLastLocation] = useState(location);
  const [shouldUnload, setShouldUnload] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const { showFooter, hideFooter, showModal, hideModal, showToast } =
    useLayoutContext();

  const closeModal = () => {
    clearError();
    hideModal();
  };

  const {
    instance,
    makeWebViewerVisible,
    setWebViewerPosition,
    hideWebViewer,
  } = useWebViewerContext();

  const handleFieldChange = useCallback((key: string, value: any) => {
    setHasUnsavedChanges(true);
    if (key.startsWith('customField_')) {
      if (value === null) {
        delete customFieldDataRef.current[key];
      } else {
        customFieldDataRef.current[key] = value;
      }
    }
  }, []);

  const templateId = templateDefaultValuesResponse?.id;

  const customFieldSavedData = useMemo(() => {
    if (!templateDefaultValuesResponse?.values) return {};
    return Object.fromEntries(
      Object.entries(templateDefaultValuesResponse.values).filter(([key]) =>
        key.startsWith('customField_'),
      ),
    );
  }, [templateDefaultValuesResponse?.values]);

  useEffect(() => {
    customFieldDataRef.current = { ...customFieldSavedData };
  }, [customFieldSavedData]);

  const fakeProject = useMemo(
    () => ({ documents: [{ id: templateId }] }),
    [templateId],
  );

  const { newInit } = useBackend(
    customFieldSavedData,
    fakeProject,
    instance,
    handleFieldChange,
  );

  const getCurrentValues = useCallback(() => {
    if (!instance) return {};
    return {
      ...extractFieldValues(instance),
      ...liveFieldValuesRef.current,
      ...customFieldDataRef.current,
    };
  }, [instance]);

  // Capture every field change by pdfElementID so checkbox toggles (and
  // any other edits PDFTron may not flush to field.getValue() before save)
  // are reliably included in getCurrentValues().
  useEffect(() => {
    if (!instance || !backendReady) return;
    const annotManager = instance.Core?.documentViewer?.getAnnotationManager();
    if (!annotManager) return;

    const onFieldChanged = (field: any, value: any) => {
      const widget = field.widgets?.[field.widgets.length - 1];
      const elementId = widget?.getCustomData('elementId');
      if (!elementId) return;

      const normalized = normalizeFieldValue(field.type, value);
      if (normalized !== null) {
        liveFieldValuesRef.current[elementId] = normalized;
      } else {
        delete liveFieldValuesRef.current[elementId];
      }
    };

    annotManager.on('fieldChanged', onFieldChanged);
    return () => annotManager.off('fieldChanged', onFieldChanged);
  }, [instance, backendReady]);

  const handleClose = () => {
    if (hasUnsavedChanges || !templateDefaultValuesResponse?.title) {
      showModal(LAYOUT_MODAL_TYPES.templateDefaultValuesModal, {
        title: 'Save or discard changes',
        inputValue: templateDefaultValuesResponse?.title,
        enableTextInput: true,
        onCancel: () => {
          closeModal();
        },
        onDiscard: async () => {
          if (!templateDefaultValuesResponse?.title) {
            await onSubmit('delete', {
              id: templateDefaultValuesResponse?.id,
            });
          }
          hideModal();
          hideFooter();
          history.replace(`/library/form-templates`);
          setConfirmedNavigation(true);
        },
        onSave: async (title: string) => {
          const valuesToWrite = getCurrentValues();
          const success = await onSubmit('update', {
            id: templateDefaultValuesResponse?.id,
            title:
              title === templateDefaultValuesResponse?.title ? null : title,
            values: valuesToWrite,
          });
          if (success) {
            templateDefaultValuesResponse!.title = title;
            hideModal();
            hideFooter();
            history.replace(`/library/form-templates`);
            setConfirmedNavigation(true);
            showToast(LAYOUT_TOAST_TYPES.success, {
              message: 'Saved form template successfully',
            });
            analyticsService.track('Form Template Saved', {
              formTemplateId: templateDefaultValuesResponse?.id,
            });
          } else {
            showToast(LAYOUT_TOAST_TYPES.error, {
              message: 'Something went wrong, please try again later.',
            });
          }
        },
        confirmButtonName: 'Save and close',
      });
    } else {
      setConfirmedNavigation(true);
      hideModal();
      hideFooter();
      history.replace(`/library/form-templates`);
    }
  };

  const handleSave = async () => {
    const valuesToWrite = getCurrentValues();
    if (templateDefaultValuesResponse?.title) {
      const success = await onSubmit('update', {
        id: templateDefaultValuesResponse?.id,
        values: valuesToWrite,
      });
      if (success) {
        hideModal();
        setHasUnsavedChanges(false);
        showToast(LAYOUT_TOAST_TYPES.success, {
          message: 'Saved form template successfully',
        });
      } else {
        showToast(LAYOUT_TOAST_TYPES.error, {
          message: 'Something went wrong, please try again later.',
        });
      }
    } else {
      showModal(LAYOUT_MODAL_TYPES.templateDefaultValuesModal, {
        title: 'Save form template',
        inputValue: templateDefaultValuesResponse?.title,
        enableTextInput: true,
        onCancel: () => {
          closeModal();
        },
        onSave: async (title: string) => {
          const success = await onSubmit('update', {
            id: templateDefaultValuesResponse?.id,
            title:
              title === templateDefaultValuesResponse?.title ? null : title,
            values: valuesToWrite,
          });
          if (success) {
            templateDefaultValuesResponse!.title = title;
            hideModal();
            setHasUnsavedChanges(false);
            showToast(LAYOUT_TOAST_TYPES.success, {
              message: 'Saved form template successfully',
            });
            analyticsService.track('Form Template Saved', {
              formTemplateId: templateDefaultValuesResponse?.id,
            });
          } else {
            showToast(LAYOUT_TOAST_TYPES.error, {
              message: 'Something went wrong, please try again later.',
            });
          }
        },
        confirmButtonName: 'Save',
      });
    }
  };

  const handleDuplicate = () => {
    showModal(LAYOUT_MODAL_TYPES.templateDefaultValuesModal, {
      title: 'Duplicate form template',
      inputValue: templateDefaultValuesResponse?.title,
      enableTextInput: true,
      onCancel: () => {
        closeModal();
      },
      onSave: async (title: string) => {
        const { success, newId } = await onSubmit('duplicate', {
          id: templateDefaultValuesResponse?.id,
          title,
        });
        if (success) {
          hideModal();
          hideFooter();
          history.push(`/form-template/${newId}`, { duplicate: true });
          setConfirmedNavigation(true);
        } else {
          showToast(LAYOUT_TOAST_TYPES.error, {
            message: 'Something went wrong, please try again later.',
          });
        }
      },
      confirmButtonName: 'Duplicate',
    });
  };

  const handleRename = () => {
    showModal(LAYOUT_MODAL_TYPES.templateDefaultValuesModal, {
      title: 'Rename form template',
      inputValue: templateDefaultValuesResponse?.title,
      enableTextInput: true,
      onCancel: () => {
        closeModal();
      },
      onSave: async (title: string) => {
        const success = await onSubmit('update', {
          id: templateDefaultValuesResponse?.id,
          title,
        });
        if (success) {
          hideModal();
          templateDefaultValuesResponse!.title = title;
          showToast(LAYOUT_TOAST_TYPES.success, {
            message: 'Renamed form template successfully',
          });
        } else {
          showToast(LAYOUT_TOAST_TYPES.error, {
            message: 'Something went wrong, please try again later.',
          });
        }
      },
      confirmButtonName: 'Rename',
    });
  };

  const handleDelete = () => {
    showModal(LAYOUT_MODAL_TYPES.templateDefaultValuesModal, {
      title: 'Delete form template',
      onCancel: () => {
        closeModal();
      },
      onDelete: async () => {
        const success = await onSubmit('delete', {
          id: templateDefaultValuesResponse?.id,
        });
        if (success) {
          hideModal();
          hideFooter();
          history.replace(`/library/form-templates`);
          setConfirmedNavigation(true);
          showToast(LAYOUT_TOAST_TYPES.success, {
            message: 'Deleted form template successfully',
          });
        } else {
          showToast(LAYOUT_TOAST_TYPES.error, {
            message: 'Something went wrong, please try again later.',
          });
        }
      },
    });
  };

  const handleBlockedRoute = (nextLocation: Location) => {
    setLastLocation(nextLocation);
    if (!confirmedNavigation && hasUnsavedChanges) {
      handleClose();
      return false;
    }

    hideModal();
    hideFooter();
    return true;
  };

  // Block react routes
  useEffect(() => {
    if (confirmedNavigation) {
      setShouldUnload(true);
      history.push(lastLocation.pathname, lastLocation.state);
      setConfirmedNavigation(false);
      hideFooter();
    }
  }, [confirmedNavigation, lastLocation, history, shouldUnload, hideFooter]);

  // Block refresh/close tab
  useEffect(() => {
    const unload = (event: any) => {
      const e = event || window.event;
      e.preventDefault();
      if (hasUnsavedChanges && !shouldUnload) {
        e.returnValue = 'Are your sure to leave?';
      }
      if (shouldUnload) {
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', unload);

    return () => window.removeEventListener('beforeunload', unload);
  }, [hasUnsavedChanges, shouldUnload]);

  useEffect(() => {
    if (
      !templateDefaultValuesResponse ||
      !instance ||
      isLoading ||
      backendReady
    ) {
      if (!templateDefaultValuesResponse || isLoading) hideFooter();
      return;
    }

    const elements = toDocumentElements(
      templateDefaultValuesResponse.pdfTemplateTagInstances,
      templateDefaultValuesResponse.values,
    );

    newInit([
      {
        id: templateDefaultValuesResponse.id,
        title: templateDefaultValuesResponse.template.title ?? '',
        pdf: templateDefaultValuesResponse.template.originalFile,
        allElements: elements,
        pages: [],
        processedPages: [],
      },
    ]);

    setBackendReady(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    templateDefaultValuesResponse,
    instance,
    isLoading,
    templateDefaultValuesID,
    backendReady,
  ]);

  useEffect(() => {
    if (isLoading) {
      hideFooter();
      return;
    }
    showFooter(LAYOUT_FOOTER_TYPES.templateDefaultValuesDraftingFooter, {
      onClose: handleClose,
      onSave: handleSave,
      onDuplicate: handleDuplicate,
      onRename: handleRename,
      onDelete: handleDelete,
      hasUnsavedChanges,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasUnsavedChanges]);

  useEffect(() => {
    setWebViewerPosition({
      left: NAVIGATION_WIDTH,
      right: 0,
      top: 134,
      bottom: 72,
    });
    if (!isLoading) {
      makeWebViewerVisible();
    }
    return () => {
      hideWebViewer();
    };
  }, [
    setWebViewerPosition,
    makeWebViewerVisible,
    hideWebViewer,
    isLoading,
    templateDefaultValuesID,
  ]);

  if (isLoading) {
    return (
      <LoadingOverlay
        title={
          duplicate ? 'Duplicating template...' : 'Loading form template...'
        }
        subtitle="Please wait"
        relative={false}
      />
    );
  }

  return (
    <>
      <div>
        <header className="flex p-4 bg-white items-center border-gray-200">
          <div>
            <button
              onClick={handleClose}
              className="flex justify-center items-center w-8 h-8 hover:bg-gray-100 rounded-md"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="ml-2">
            <h1 className="font-semibold">
              {templateDefaultValuesResponse?.title
                ? `${templateDefaultValuesResponse?.title}`
                : 'Untitled'}
            </h1>
            <div className="whitespace-nowrap">
              <Text subdued>
                {[
                  templateDefaultValuesResponse?.template.title,
                  templateDefaultValuesResponse?.template.subtitle,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </Text>
            </div>
          </div>
        </header>
        <div className="bg-blue-50 p-4 border-y border-blue-200">
          <div className="flex justify-center text-gray-900">
            <Text>
              You&apos;re editing a form template.{' '}
              <a
                href="https://help.clio.com/hc/en-us/articles/24315924591771-Clio-Draft-Form-Templates"
                target="_blank"
                style={{
                  textDecoration: 'underline',
                }}
                rel="noreferrer"
              >
                Learn more
              </a>
            </Text>
          </div>
        </div>
        <div className="Generate"></div>
      </div>
      <Prompt when message={handleBlockedRoute} />
    </>
  );
};

export default FormTemplateDraftingContainer;
